/**
 * Lookups over the article list (ordering, courses, related reading, search). Pure, so the build's
 * static HTML uses them too; the Vite config loads this file, hence the .ts import extensions.
 */
import { COURSES, TOPICS, topicInfo, type CourseId, type TopicId } from './taxonomy.ts';
import type { ArticleMeta } from './types.ts';

const COURSE_RANK = new Map(COURSES.map((c, i) => [c.id, i]));

/** Position across all courses: Beginner 1–15, then Intermediate, Advanced, Platform Guide. */
export const sequence = (a: ArticleMeta) => (COURSE_RANK.get(a.course) ?? 9) * 1000 + a.order;

export const bySequence = (a: ArticleMeta, b: ArticleMeta) => sequence(a) - sequence(b);

/** Newest first; articles published the same day show the later lesson first. */
export function sortNewest(list: readonly ArticleMeta[]): ArticleMeta[] {
  return [...list].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt) || b.updatedAt.localeCompare(a.updatedAt) || sequence(b) - sequence(a));
}

/** Editors' popular picks first, then featured, then course order. */
export function sortPopular(list: readonly ArticleMeta[]): ArticleMeta[] {
  return [...list].sort((a, b) => Number(b.popular) - Number(a.popular) || Number(b.featured) - Number(a.featured) || bySequence(a, b));
}

export function courseArticles(list: readonly ArticleMeta[], course: CourseId): ArticleMeta[] {
  return list.filter((a) => a.course === course).sort((a, b) => a.order - b.order);
}

/** Previous and next lesson in the article's course, and its "Lesson X of N" position. */
export function lessonNav(list: readonly ArticleMeta[], a: ArticleMeta) {
  const lessons = courseArticles(list, a.course);
  const i = lessons.findIndex((l) => l.slug === a.slug);
  return { prev: lessons[i - 1] ?? null, next: lessons[i + 1] ?? null, index: i + 1, total: lessons.length };
}

/** Five articles to read next: shared topics count most, then same course, category and level; closest in the course breaks ties. */
export function relatedArticles(list: readonly ArticleMeta[], a: ArticleMeta, n = 5): ArticleMeta[] {
  const score = (b: ArticleMeta) =>
    b.topics.filter((t) => a.topics.includes(t)).length * 3 + (b.course === a.course ? 2 : 0) + (b.category === a.category ? 1 : 0) + (b.level === a.level ? 1 : 0);
  return list
    .filter((b) => b.slug !== a.slug)
    .map((b) => ({ b, s: score(b), d: Math.abs(sequence(b) - sequence(a)) }))
    .sort((x, y) => y.s - x.s || x.d - y.d)
    .slice(0, n)
    .map((x) => x.b);
}

export function topicCounts(list: readonly ArticleMeta[]): Map<TopicId, number> {
  const m = new Map<TopicId, number>(TOPICS.map((t) => [t.id, 0]));
  for (const a of list) for (const t of a.topics) m.set(t, (m.get(t) ?? 0) + 1);
  return m;
}

/**
 * Plain-text search: every word of the query must appear in the title, headings, description or
 * topics. Title matches rank highest.
 */
export function searchArticles(list: readonly ArticleMeta[], query: string): ArticleMeta[] {
  const words = query.toLowerCase().split(/[^a-z0-9%$/.]+/).filter((w) => w.length > 1 || /\d/.test(w));
  if (!words.length) return [...list];
  const scored: { a: ArticleMeta; s: number }[] = [];
  for (const a of list) {
    const title = a.title.toLowerCase();
    const heads = a.headings.join(' ').toLowerCase();
    const desc = a.description.toLowerCase();
    const topics = a.topics.map((t) => topicInfo(t)?.title ?? t).join(' ').toLowerCase();
    let s = 0;
    for (const w of words) {
      if (title.includes(w)) s += 6;
      else if (topics.includes(w)) s += 3;
      else if (heads.includes(w)) s += 3;
      else if (desc.includes(w)) s += 2;
      else {
        s = -1;
        break;
      }
    }
    if (s > 0) scored.push({ a, s });
  }
  return scored.sort((x, y) => y.s - x.s || bySequence(x.a, y.a)).map((x) => x.a);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-19" → "19 Oct 2026" (no time zone shift). */
export function formatDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return `${d} ${MONTHS[(m ?? 1) - 1]} ${y}`;
}
